import React, { useState, useRef, useEffect } from 'react';
import getConfig from 'next/config';
import Link from 'next/link';
import { useRouter } from 'next/router';
import Request from '@apester/requests';
import AuthForm from '../../components/AuthForm/AuthForm';
import InputField from '../../components/InputField/InputField';
import AuthLayout from '../../layouts/AuthLayout/AuthLayout';
import trackEvent from '../../utils/event-helper';
import { useStateValue, setUser, setLoading } from '../../state/stateProvider';
import { EMAIL_MISSING, SIGNUP_REJECTED_ERRORS, INVITE_ONLY_MESSAGE } from '../../utils/auth-utils';
import { linkifyContactEmail } from '../../utils/linkify';

const { publicRuntimeConfig: { config } } = getConfig();

const LoginPage = () => {
  const router = useRouter();
  const [error, setError] = useState<string>();
  const [emailError, setEmailError] = useState<string>();
  const emailInput = useRef(null);
  const passwordInput = useRef(null);
  const [, dispatch] = useStateValue();

  // A rejected Google signup redirects here with ?error=signup_invite_only — surface the reason.
  useEffect(() => {
    if (!router.isReady) return;
    if (SIGNUP_REJECTED_ERRORS.includes(router.query.error as string)) {
      setError(INVITE_ONLY_MESSAGE);
    }
  }, [router.isReady, router.query.error]);

  const login = async (email, password, captcha) => {
    const response = await Request.Post(new URL(`${config.USERS_URL}/login`),
      { username: email, password, captcha }, { withCredentials: true });
    return response;
  };

  const getAccountType = (user) => {
    if (user.google) {
      return 'google';
    }
    return 'email';
  };

  const buildSigninEventProperties = (user) => ({
    browser: navigator.appVersion,
    username: user.displayName,
    userEmail: user.emails[0],
    userId: user.userId,
    publisherId: user.defaultPublisher,
    ip: '',
    accountType: getAccountType(user),
    method: 'apester',
  });

  const validateEmail = () => {
    setEmailError(!emailInput?.current?.value && EMAIL_MISSING);
  };

  const submitLogin = async (recaptchaVal = undefined) => {
    setLoading(dispatch, true);
    const userLoginData = {
      email: emailInput.current.value,
      password: passwordInput.current.value,
    };
    const clickedEventData = {
      username: userLoginData.email,
      userEmail: userLoginData.email,
      browser: navigator.appVersion,
    };
    trackEvent('user_login_clicked', clickedEventData);
    setError(undefined);
    try {
      const loginResp = await login(userLoginData.email, userLoginData.password, recaptchaVal || 'some-mock-recaptcha');
      const user = loginResp.payload;
      const eventProperties = buildSigninEventProperties(user);
      trackEvent('user_login_complete', eventProperties);
      setUser(dispatch, user);
      setLoading(dispatch, false);
    } catch (e) {
      const errorData = {
        browser: navigator.appVersion,
        username: userLoginData.email,
        ip: '',
        reason: e.message,
      };
      trackEvent('user_login_fail', errorData);
      setError(e.message);
      setLoading(dispatch, false);
    }
  };

  return (
    <>
      <AuthForm className="loginForm"
      submitCallback={submitLogin}
      submitLabel="sign in"
      getEmail={() => emailInput.current?.value}>
          <InputField onClick={() => trackEvent('username_email_login_clicked')}
            hasError={emailError}
            onBlur={validateEmail}
            onChange={validateEmail}
            refElement={emailInput}
            label="Email"
            style={{ gridRow: 1 }}/>
          <InputField onClick={() => trackEvent('password_login_clicked')}
            refElement={passwordInput} label="Password" type="password" style={{ gridRow: 3 }}/>
          <div className="linkContainer" style={{ gridRow: 5 }}>
            <Link href="/auth/forgot-password">
              <a className="link" onClick={() => trackEvent('forgot_password_clicked')}>
                forgot password?
              </a>
            </Link>
          </div>
      </AuthForm>
      <div className="error">
        {linkifyContactEmail(error)}
      </div>
    </>
  );
};

LoginPage.layout = AuthLayout;

export default LoginPage;
